import { useEffect, useState } from 'react';
import User from 'app/constants/schemas/User';
import { useAuthUser } from 'app/components/auth/AuthUserContext';

/**
 * Require authentication, as hook.
 *
 * When already authenticated, or the authentication becomes true it
 * will trigger the `onAuthenticated` callback, with the current user.
 *
 * Provides `{ wasAuthenticated, isAuthenticated }` Where:
 *
 *   - isAuthenticated (boolean)
 *
 *     the current authentication status
 *
 *   - wasAuthenticated (boolean):
 *
 *     true unless the user was unauthenticated during the component using
 *     this hook was on screen. This extra bit of state tracking is
 *     for convenience for the contents of the page. A UI element
 *     might only be required if prompting for a login, but shouldn't
 *     disappear immediately after authentication.
 */

const useRequireAuthentication = (onAuthenticated?: (user: User, wasAuthenticated: boolean) => void) => {
  const authUser = useAuthUser();
  const [wasAuthenticated, setWasAuthenticated] = useState(Boolean(authUser));

  useEffect(() => {
    if (authUser !== null && onAuthenticated) {
      onAuthenticated(authUser, wasAuthenticated);
    }

    if (authUser === null && wasAuthenticated) {
      setWasAuthenticated(false);
    }
  }, [onAuthenticated, authUser, wasAuthenticated]);

  return {
    isAuthenticated: Boolean(authUser),
    wasAuthenticated,
  };
};

export default useRequireAuthentication;
